import _ from "lodash"

import { ReservedFields, ReservedSections } from "@/onboarding/features/product/models/Product"
import { getField, getSectionFieldNames } from "@/onboarding/features/product/selectors"

export interface ImportSection {
  name: string
  title: string
  fieldNames: string[]
}

// Section titles for the component-list sections. The single source of truth —
// product_components/Section.tsx renders its headers from this map too.
export const COMPONENT_SECTION_TITLES: Partial<Record<ReservedFields, string>> = {
  [ReservedFields.VENUE_COMPONENTS]: "Sites, Venues and Attractions",
  [ReservedFields.LOGISTICAL_COMPONENTS]: "Transportation",
  [ReservedFields.REFRESHMENT_COMPONENTS]: "Refreshments and Meals",
  [ReservedFields.RESTAURANT_COMPONENTS]: "Restaurants",
  [ReservedFields.ENTERTAINMENT_COMPONENTS]: "Entertainment",
  [ReservedFields.GUIDE_COMPONENTS]: "Escorts and Guides",
  [ReservedFields.HEADSET_COMPONENTS]: "Headsets",
  [ReservedFields.ATTACHMENTS]: "Forms, Waivers, Documents and Attachments",
}

const COMPONENT_SECTIONS = [
  ReservedFields.VENUE_COMPONENTS,
  ReservedFields.LOGISTICAL_COMPONENTS,
  ReservedFields.REFRESHMENT_COMPONENTS,
  ReservedFields.RESTAURANT_COMPONENTS,
  ReservedFields.ENTERTAINMENT_COMPONENTS,
  ReservedFields.GUIDE_COMPONENTS,
  ReservedFields.ATTACHMENTS,
]

// The section registry behind "Import from Tour": one entry per section the
// per-section Importer icons cover, with the same field lists. Settlement and
// Forecasts stay excluded (pricing sections with their own copy semantics).
// Tour Costs contributes only the fields no other entry owns: the overridden
// ratesheet in overridden mode, otherwise just the operator fee ratesheet —
// the calculated-mode inputs are the component sections and tour stops
// already listed.
export const getImportSections = (state): ImportSection[] => {
  const selectedNetRatesheet = getField(ReservedFields.SELECTED_NET_RATESHEET_FIELD)(state).value

  const tourCostFields =
    selectedNetRatesheet === ReservedFields.OVERRIDDEN_NET_RATESHEET
      ? [ReservedFields.OVERRIDDEN_NET_RATESHEET]
      : [ReservedFields.OPERATOR_FEE_RATESHEET]

  return [
    {
      name: ReservedSections.TOUR_DETAILS,
      title: "Tour Details",
      fieldNames: getSectionFieldNames.tourDetails(state),
    },
    {
      name: ReservedSections.DESCRIPTION,
      title: "Web Content",
      fieldNames: getSectionFieldNames.description(state),
    },
    {
      name: ReservedSections.ADDITIONAL_DETAILS,
      title: "Additional Details",
      fieldNames: getSectionFieldNames.additionalDetails(state),
    },
    {
      name: ReservedSections.TOUR_STOPS,
      title: "Tour Itinerary",
      fieldNames: [...getSectionFieldNames.tourLocation(state), ReservedFields.TOUR_STOPS],
    },
    {
      name: ReservedSections.EXTRA_FEES,
      title: "Extra Fees",
      fieldNames: [ReservedFields.EXTRA_FEES],
    },
    {
      name: ReservedSections.HEADSET_COMPONENTS,
      title: "Headsets",
      fieldNames: [ReservedFields.HEADSET_COMPONENTS, ReservedFields.HEADSET_FEE_RATESHEET],
    },
    {
      name: ReservedSections.TOUR_IMAGES,
      title: "Tour Images and Video",
      fieldNames: [ReservedFields.TOUR_IMAGES],
    },
    ...COMPONENT_SECTIONS.map((fieldName) => ({
      name: fieldName as string,
      title: COMPONENT_SECTION_TITLES[fieldName],
      fieldNames: [fieldName as string],
    })),
    {
      name: ReservedSections.TOUR_COST,
      title: "Tour Costs",
      fieldNames: tourCostFields,
    },
  ].filter((section) => !_.isEmpty(section.fieldNames))
}
